import React, { useEffect, useState } from 'react';

import { Botao, Field, Input, Modal } from '../../../ui';

interface ModalNovoTipoTermoProps {
  aberto: boolean;
  processando?: boolean;
  onConfirmar: (nome: string) => void;
  onCancelar: () => void;
}

export const MINIMO_NOME = 3;

/**
 * Cadastro de um tipo de termo novo (at-40, item 43; reescrito na at-69).
 *
 * O catálogo de tipos passou a ser do SAAC, então criar tipo deixou de depender de
 * o SGA ter um. Aqui só se dá o **nome**: o código é gerado pelo backend (maior
 * conhecido + 1), e o texto das cláusulas é escrito depois, na ficha do template.
 *
 * ## O que mudou na at-69
 *
 *   1. **Virou o `Modal` do kit** — era o `.confirmacao-overlay` + `modalDistrato.css`
 *      (dois CSS emprestados de outros modais), com listener de `Escape` próprio e
 *      `aria-modal="true"` **sem prender o foco**.
 *   2. **O mínimo é anunciado desde o início**, na dica do campo: antes o aviso só
 *      aparecia depois de digitar, e quem via o botão travado não sabia o porquê.
 *
 * ✅ **O contrato de props não mudou** (`aberto`, `processando`, `onConfirmar(nome)`,
 * `onCancelar`), nem a regra do mínimo de 3 — é migração de envelope (D7).
 */
const ModalNovoTipoTermo: React.FC<ModalNovoTipoTermoProps> = ({
  aberto,
  processando,
  onConfirmar,
  onCancelar,
}) => {
  const [nome, setNome] = useState('');

  // Limpa ao reabrir: nome digitado num cadastro não pode reaparecer no seguinte.
  useEffect(() => {
    if (aberto) setNome('');
  }, [aberto]);

  const valido = nome.trim().length >= MINIMO_NOME;
  const erro =
    nome.length > 0 && !valido ? `Informe um nome com pelo menos ${MINIMO_NOME} caracteres.` : '';

  return (
    <Modal
      aberto={aberto}
      titulo="Novo tipo de termo"
      descricao="O tipo nasce sem texto e desativado. Ele passa a valer para geração quando você escrever as cláusulas e marcá-lo como ativo."
      tamanho="sm"
      ocupado={processando}
      aoFechar={onCancelar}
      alinharRodape="direita"
      rodape={
        <>
          <Botao variante="secundario" disabled={processando} onClick={onCancelar}>
            Voltar
          </Botao>
          <Botao carregando={processando} disabled={!valido} onClick={() => onConfirmar(nome.trim())}>
            Cadastrar tipo
          </Botao>
        </>
      }
    >
      <Field
        rotulo="Nome do tipo de termo"
        obrigatorio
        dica={`Aparece nas telas. Mínimo de ${MINIMO_NOME} caracteres. O código é gerado pelo sistema.`}
        erro={erro}
        campo="nome"
      >
        <Input
          autoFocus
          value={nome}
          disabled={processando}
          placeholder="Ex.: TERMO DE RESPONSABILIDADE - VIDROS"
          onChange={(evento) => setNome(evento.target.value)}
        />
      </Field>
    </Modal>
  );
};

export default ModalNovoTipoTermo;
